import type { TourStep } from '@/components/ui/Tour';
import { BOOKINGS_MANAGEMENT_TOUR_STEP_IDS, TOUR_CENTERED_STEP_SELECTOR } from '../tour-constants';
import { TourStepContent } from './StepContent';

export interface BookingsListTourContext {
  isStarterPlan: boolean;
  hasBookings: boolean;
}

function getBookingsListManageStepCopy(ctx: BookingsListTourContext): {
  title: string;
  description: string;
} {
  if (!ctx.hasBookings) {
    if (ctx.isStarterPlan) {
      return {
        title: 'Your bookings will appear here',
        description:
          'Once families start booking, enrollments show up in this list. On Starter, confirm each booking manually after you have collected payment from the student.',
      };
    }
    return {
      title: 'Your bookings will appear here',
      description:
        'Once families start booking, enrollments show up in this list. On Professional or Enterprise, enable online payments in Payments so bookings confirm automatically when students pay online — you can still confirm pending bookings manually when needed.',
    };
  }

  if (ctx.isStarterPlan) {
    return {
      title: 'Confirm and manage',
      description:
        'Review each row, open booking details, and use bulk actions to confirm pending bookings after you have collected payment, or cancel selections. Pending refund requests appear above the list when students ask for money back after a cancellation.',
    };
  }

  return {
    title: 'Confirm and manage',
    description:
      'Review each row, open booking details, and use bulk actions to confirm pending bookings or cancel selections. Paid online bookings confirm automatically when Payments is set up. Pending refund requests appear above the list when students ask for money back after a cancellation.',
  };
}

function getBookingsListIntroCopy(ctx: BookingsListTourContext): {
  title: string;
  description: string;
} {
  if (ctx.isStarterPlan) {
    return {
      title: 'Managing bookings',
      description:
        'This is where you confirm enrollments and track payment status for lesson and camp bookings across your academy.',
    };
  }

  return {
    title: 'Managing bookings',
    description:
      'This is where you confirm enrollments, track payment status, manage waitlists, and handle expression-of-interest and private-lesson requests across your academy.',
  };
}

function getBookingsListTabsStepCopy(ctx: BookingsListTourContext): {
  title: string;
  description: string;
} {
  if (ctx.isStarterPlan) {
    return {
      title: 'Booking tabs',
      description:
        'Waitlist, Expressions of interest, and Private requests are available on Professional and Enterprise plans.',
    };
  }

  return {
    title: 'Booking tabs',
    description:
      'Use Bookings for enrollments, Waitlist when lessons are full, Expressions of interest for invitation-only programs, and Private requests for guardian-initiated private packages.',
  };
}

export function getBookingsListTourSteps(ctx: BookingsListTourContext): TourStep[] {
  const manageStep = getBookingsListManageStepCopy(ctx);
  const introStep = getBookingsListIntroCopy(ctx);
  const tabsStep = getBookingsListTabsStepCopy(ctx);

  return [
    {
      content: (
        <TourStepContent title={introStep.title} description={introStep.description} />
      ),
      selectorId: TOUR_CENTERED_STEP_SELECTOR,
    },
    {
      content: (
        <TourStepContent title={tabsStep.title} description={tabsStep.description} />
      ),
      selectorId: BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.BOOKINGS_TABS,
      position: 'bottom',
    },
    {
      content: (
        <TourStepContent
          title="Find bookings"
          description="Search by student name and open Filters to narrow by term, lesson, status, payment, and sort order. Results load in pages from the server."
        />
      ),
      selectorId: BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.BOOKINGS_FILTERS,
      position: 'bottom',
    },
    {
      content: (
        <TourStepContent title={manageStep.title} description={manageStep.description} />
      ),
      selectorId: BOOKINGS_MANAGEMENT_TOUR_STEP_IDS.BOOKINGS_LIST,
      position: 'top',
    },
  ];
}

/** Default registry steps (Professional+ with bookings) for anchor readiness checks. */
export const bookingsListTourSteps: TourStep[] = getBookingsListTourSteps({
  isStarterPlan: false,
  hasBookings: true,
});
